/**
 * @file layoutFilter.ts
 *
 * Pure functions that decide which parts of a computed device layout should be
 * dispatched back to Redux state. Extracted from calculateDevicesMiddleware so
 * the logic can be unit-tested without standing up the middleware stack.
 *
 * No side effects, no DOM access (micPathExists is injected by the caller so
 * tests can control it without touching document).
 */

import toolIndex from 'toolConstants';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ReduxAction = { type: string; payload?: any };

/** All decision inputs derived from state + action, computed once and passed around. */
export interface LayoutContext {
  areaState: any;
  micModel: string;
  currentDevice: string;
  customized: Record<string, any>;
  /** Device types (not the current one) that are missing plot data and need refreshing. */
  otherPlots: string[];
  isViewChange: boolean;
  toAcoustics: boolean;
  inAcoustics: boolean;
  isDimensionChange: boolean;
  /** Ceiling-tile block / unblock / resize — relocates BOTH mics and speakers off the tile. */
  isCeilingTileChange: boolean;
  isOnlySpeakerOperation: boolean;
  shouldPreserveCameraPlots: boolean;
  /** Whether cameras should be included based on current view and preserve rules. */
  withCameras: boolean;
  BAR: Set<string>;
  HAS_CAMERA: Set<string>;
  TABLETOP: Set<string>;
  /**
   * True when the current mic COULD be a path-sampled ceiling/pendant placement — either a
   * TABLETOP mic (ttm_x) or a pole-mounted ceiling mic (tcm_x et al — CEILING_MOUNTED but
   * not the grid-only CEILING_TILE/BMA360D) on a round/keystone/vshape table. (Not further
   * gated on coverage.focused — see the comment at its definition.) Both read positions via
   * `getPointAtLength` on an SVG path (#micPath) that MuiTable re-renders on every view
   * mount, so both are vulnerable to the same DOM-timing race the guards below protect
   * against.
   */
  isPathDependentMic: boolean;
}

// ---------------------------------------------------------------------------
// Context builder
// ---------------------------------------------------------------------------

/** Derives all decision flags from state and action. */
export const buildLayoutContext = (
  state: any,
  action: ReduxAction,
  areaState: any,
  constants: any,
): LayoutContext => {
  const { BAR, HAS_CAMERA, TABLETOP, CEILING_MOUNTED, CEILING_TILE } = constants.equipment.micCategories;
  const currentDevice: string = state.ui.currentDevice || state.container.currentDevice || 'microphones';
  const { customized = {} } = areaState;
  const currentView: string = state.ui?.currentView;
  const hideDevices: string[] = constants?.hideDevices ?? [];
  const micModel: string = areaState.microphones?.model;

  // Note: deliberately NOT gated on coverage.focused. The round/keystone/vshape path-based
  // branch in calculateMicPlacement only runs when focused, but getting that condition wrong
  // here just means the guard also protects a mic that's actually on the (always-stable) grid
  // path — harmless. Under-protecting (requiring focused and being wrong about it) is what
  // actually caused the mic to keep moving, so this errs toward over-protecting instead.
  const isPathBasedCeilingMic = Boolean(
    CEILING_MOUNTED?.has(micModel) &&
    !CEILING_TILE?.has(micModel) &&
    ['round', 'keystone', 'vshape'].includes(areaState.table?.shape),
  );
  const isPathDependentMic = TABLETOP.has(micModel) || isPathBasedCeilingMic;

  const needsPlotData: Record<string, boolean> = ['microphones', 'speakers', 'cameras'].reduce(
    (acc: Record<string, boolean>, key: string) => {
      const device = areaState[key] ?? {};
      acc[key] = Object.keys(device.plotData ?? {}).length === 0;
      return acc;
    },
    {},
  );
  const otherPlots = Object.keys(needsPlotData).filter(
    (k) => k !== currentDevice && needsPlotData[k],
  );

  const isViewChange = action.type === 'ui/setCurrentView';
  const toAcoustics = isViewChange && action.payload === 'acoustics';
  const inAcoustics = currentView === 'acoustics';
  const isResetOperation = action.type.includes('resetArea') || action.type.includes('resetDevices');
  const isDimensionChange =
    action.type.includes('setDimensions') || action.type.includes('setCeilingHeight');
  const isCeilingTileChange = action.type.includes('setCeilingTiles');

  const hasExistingCameraPlots = Object.keys(areaState.cameras?.plotData ?? {}).length > 0;
  const shouldPreserveCameraPlots = isViewChange && hasExistingCameraPlots && hideDevices.includes(currentView);

  const camerasNeedInitialLayout = areaState.cameras?.hasCamera && !areaState.cameras?.layout?.length;
  const isInitialCalculation =
    camerasNeedInitialLayout &&
    ((isViewChange && action.payload === 'floorplan') || isDimensionChange);
  const forceCameraUpdate = isDimensionChange && Boolean(areaState.cameras?.hasCamera);
  const shouldSkipCameraUpdate =
    !isResetOperation &&
    !isInitialCalculation &&
    !shouldPreserveCameraPlots &&
    !isDimensionChange &&
    !forceCameraUpdate &&
    hideDevices.includes(currentView);

  const hasRemoveDeviceError = Boolean(state.ui?.error?.['130']);
  const isResetInAcoustics = action.type.includes('resetArea') && inAcoustics;
  const isOnlySpeakerOperation =
    !isResetInAcoustics &&
    (currentDevice === 'speakers' || hasRemoveDeviceError) &&
    !customized.microphones &&
    !needsPlotData.microphones &&
    !customized.cameras;

  return {
    areaState,
    micModel,
    currentDevice,
    customized,
    otherPlots,
    isViewChange,
    toAcoustics,
    inAcoustics,
    isDimensionChange,
    isCeilingTileChange,
    isOnlySpeakerOperation,
    shouldPreserveCameraPlots,
    withCameras: !shouldSkipCameraUpdate, // shouldPreserveCameraPlots is already a term in shouldSkipCameraUpdate
    BAR,
    HAS_CAMERA,
    TABLETOP,
    isPathDependentMic,
  };
};

// ---------------------------------------------------------------------------
// One named function per selection rule — each name states the business reason
// ---------------------------------------------------------------------------

/** Customised bar mic: camera must follow the mic's new position. */
export const keysForCustomisedBarMic = ({ otherPlots }: LayoutContext): string[] =>
  ['microphones', 'speakers', 'cameras', ...otherPlots];

/** Bar with customised camera: video bars (built-in cam) update all; audio bars update camera only. */
export const keysForCustomisedBarCamera = ({ micModel, otherPlots, HAS_CAMERA }: LayoutContext): string[] => {
  const base = HAS_CAMERA.has(micModel) ? ['microphones', 'speakers', 'cameras'] : ['cameras'];
  return [...base, ...otherPlots];
};

/** Speaker-only: another device's action fired while the speaker tab is active. */
export const keysForSpeakerOnly = ({ otherPlots }: LayoutContext): string[] =>
  ['speakers', ...otherPlots.filter((p) => p !== 'microphones' && p !== 'cameras')];

/** Switching TO acoustics: always refresh mic + speaker plots. Optionally preserve camera plots. */
export const keysForAcousticsTransition = ({ currentDevice, customized, shouldPreserveCameraPlots }: LayoutContext): string[] => {
  const cams = !customized[currentDevice] && shouldPreserveCameraPlots ? ['cameras'] : [];
  return ['microphones', 'speakers', ...cams];
};

/** Already IN acoustics with a customised device: keep mic/speaker plots in sync. */
export const keysInAcousticsWithCustomisedDevice = ({ currentDevice, customized, areaState }: LayoutContext): string[] => {
  // PIG-620: moving the camera in Acoustics must recompute the camera layout AND its coverage
  // plot at the dropped position. This branch previously hardcoded mic/speaker keys and never
  // included 'cameras', so the camera icon moved but its coverage plot stayed at the old spot
  // ("moving the camera does not function correctly"). A camera move must not disturb the
  // mic/speaker layouts, so refresh cameras only. (Bar cameras are handled earlier by
  // keysForCustomisedBarCamera; this reaches standalone cameras.)
  if (currentDevice === 'cameras') {
    return ['cameras'];
  }
  const speakerLayoutExists = (areaState.speakers?.layout?.length ?? 0) > 0;
  if (currentDevice === 'microphones' && !customized.speakers && speakerLayoutExists) {
    return ['microphones']; // speakers untouched — preserve their existing layout
  }
  return ['microphones', 'speakers'];
};

/** User-positioned device drives the update; include any other plots that need refreshing. */
export const keysForCustomisedDevice = ({ currentDevice, otherPlots, withCameras }: LayoutContext): string[] => {
  const devices = [currentDevice, ...otherPlots];
  return withCameras ? devices : devices.filter((d) => d !== 'cameras');
};

/**
 * Ceiling-tile change (block / unblock / resize): blocking a tile under a placed device
 * relocates it to another tile, and this can happen to a mic AND a speaker independently of
 * which device tab is active or customised. Always refresh both tile-device layouts so the
 * relocation is persisted immediately — otherwise a speaker pushed off a newly-blocked tile
 * keeps its stale position until the user happens to open the Speakers tab.
 */
export const keysForCeilingTileChange = ({ otherPlots }: LayoutContext): string[] =>
  ['microphones', 'speakers', ...otherPlots];

/** Default: refresh every device type that needs plot data; camera inclusion depends on view. */
export const keysDefault = ({ otherPlots, withCameras }: LayoutContext): string[] => {
  const base = ['microphones', 'speakers', ...otherPlots];
  return withCameras ? [...base, 'cameras'] : base;
};

// ---------------------------------------------------------------------------
// Dispatcher — reads like a plain list of conditions
// ---------------------------------------------------------------------------

export const selectKeys = (ctx: LayoutContext, deviceLayout: Record<string, any>): string[] => {
  if (ctx.customized.microphones && ctx.BAR.has(ctx.micModel))       return keysForCustomisedBarMic(ctx);
  if (ctx.customized.cameras    && ctx.BAR.has(ctx.micModel))        return keysForCustomisedBarCamera(ctx);
  if (ctx.isCeilingTileChange)                                       return keysForCeilingTileChange(ctx);
  if (ctx.isOnlySpeakerOperation)                                     return keysForSpeakerOnly(ctx);
  if (ctx.toAcoustics)                                                return keysForAcousticsTransition(ctx);
  if (ctx.inAcoustics && ctx.customized[ctx.currentDevice])          return keysInAcousticsWithCustomisedDevice(ctx);
  if (ctx.customized[ctx.currentDevice])                             return keysForCustomisedDevice(ctx);
  if (ctx.isDimensionChange && ctx.areaState.cameras?.hasCamera)     return Object.keys(deviceLayout);
  return keysDefault(ctx);
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Determines which parts of a computed device layout should be dispatched, based on current
 * view, customisation state, and the action that triggered recalculation.
 *
 * Pure function — no side effects, no DOM access (micPathExists is injected so tests can
 * control it without touching document).
 *
 * @param state         - Current Redux state
 * @param deviceLayout  - Full layout returned by getDeviceLayouts (collisions/errors stripped)
 * @param action        - The Redux action that triggered recalculation
 * @param micPathExists - Whether the #micPath SVG element currently exists in the DOM
 * @returns Subset of deviceLayout containing only the device keys that should be updated
 */
export const getFilteredDeviceLayout = (
  state: any,
  deviceLayout: Record<string, any>,
  action: ReduxAction,
  micPathExists: boolean,
): Record<string, any> => {
  const activeTab: string = state.container?.activeTab;
  const areaState = state.container?.areas[activeTab];
  const constants = toolIndex[state.ui?.tool]?.constants;

  const ctx = buildLayoutContext(state, action, areaState, constants);

  const pickKeys = (keyList: string[]): Record<string, any> =>
    Object.fromEntries(
      [...new Set(keyList)].filter((k) => k in deviceLayout).map((k) => [k, deviceLayout[k]]),
    );

  let filteredLayout = pickKeys(selectKeys(ctx, deviceLayout));

  // Guard: when the mic's SVG sampling path is absent from the DOM, calculateMicPlacement reads
  // length=0 → collapses/misplaces the layout (table mics: count collapses to 1; path-based
  // ceiling mics on round/keystone/vshape tables: positions collapse to areaCenter or stack).
  // Drop the microphone update to preserve existing layout. Covers both TABLETOP (ttm_x) and
  // pole-mounted ceiling mics (tcm_x et al) that sample #micPath — see isPathDependentMic.
  if (ctx.isPathDependentMic && filteredLayout.microphones && !micPathExists) {
    const { microphones: _dropped, ...rest } = filteredLayout;
    filteredLayout = rest;
  }

  // Guard (PIG-423): preserve TABLETOP mic layout when navigating AWAY from the microphones
  // view (but NOT when landing on it — that transition is the sanctioned moment for the
  // microphones view to recalc things like coverage density; see
  // calculateDevicesMiddleware.preserveMics.test.ts, "KEEPS microphones on
  // setCurrentView('microphones')"). Even when micPath IS in the DOM, MuiTable's first render
  // after (re)mounting still uses local tableCenter=(0,0), so getPointAtLength returns shifted
  // positions on transitions like equipmentlist→speakers.
  const isViewChangeAwayFromTabletopMic = ctx.TABLETOP.has(ctx.micModel) &&
    ctx.isViewChange &&
    (action as any).payload !== 'microphones' &&
    (ctx.areaState?.microphones?.layout?.length ?? 0) > 0;

  // Guard: preserve path-based ceiling mic layout (tcm_x et al, round/keystone/vshape tables)
  // on every view change, in EITHER direction. Unlike TABLETOP, there's no view where this
  // placement needs a live navigation-triggered recalc (coverage density lives on the
  // microphones panel regardless of mic type, and isn't gated behind this path-sampling
  // recompute) — so this one isn't given the "into microphones" exception. This is the fix for
  // the mic visibly sliding left/right when returning from the equipment table to a round-table
  // ceiling mic's view: the exception above (kept for TABLETOP compatibility) was letting the
  // stale-tableCenter recalc through on arrival.
  const isViewChangeNearCeilingPathMic = ctx.isPathDependentMic &&
    !ctx.TABLETOP.has(ctx.micModel) &&
    ctx.isViewChange &&
    (ctx.areaState?.microphones?.layout?.length ?? 0) > 0;

  if ((isViewChangeAwayFromTabletopMic || isViewChangeNearCeilingPathMic) && filteredLayout.microphones) {
    const { microphones: _dropped, ...rest } = filteredLayout;
    filteredLayout = rest;
  }

  // Guard (PIG-446): navigation must never reposition already-placed speakers.
  // Entering the Acoustics view re-runs the layout pass (keysForAcousticsTransition pulls in
  // 'speakers' to refresh plots), and customised CEILING speakers get re-snapped to freshly
  // computed tile-centre coords (see the speaker layout block in roomCalculations). With a
  // BMA360D ceiling-tile mic the mic's own height/coverage recalc fires a second layout pass,
  // so the speakers visibly jump and then settle on a different position — only with BMA360D,
  // because other mic types don't trigger that extra recalc.
  // Strip ONLY the positional fields here: the shallow per-device merge in updateDevice
  // (ducks/room.ts) then preserves the existing layout/qty/targetPoint while plotData and
  // coverageDensity still refresh for the acoustics heatmap. Only explicit room/table/device
  // mutations (setDimensions, setCeilingTiles, setSpeakers, …) — not navigation — move speakers.
  //
  // Originally only guarded navigating AWAY from 'speakers' (an earlier version of this rule
  // deliberately let navigating TO 'speakers' recompute, on the assumption that landing on the
  // tab was a safe moment to refresh). That assumption doesn't hold for round/keystone/vshape
  // tables: MuiTable's #spkrPath sampling can be stale immediately after the floorplan SVG
  // remounts (e.g. equipmentlist→speakers), regardless of which view is being entered — so the
  // guard now applies on every view change, in either direction.
  const isViewChangeNearSpeakers = ctx.isViewChange &&
    (ctx.areaState?.speakers?.layout?.length ?? 0) > 0;
  if (isViewChangeNearSpeakers && filteredLayout.speakers) {
    const { layout: _layout, qty: _qty, targetPoint: _targetPoint, ...speakerRest } = filteredLayout.speakers;
    filteredLayout = { ...filteredLayout, speakers: speakerRest };
  }

  return filteredLayout;
};

/**
 * Merges calculated device layouts into a copy of state so the equipment-list selector
 * can read freshly computed device data without waiting for a Redux dispatch round-trip.
 *
 * Pure function — returns a new state object, does not mutate.
 *
 * @param state          - Current Redux state
 * @param filteredLayout - Device layout subset returned by getFilteredDeviceLayout
 * @returns New state object with updated device layouts merged into the active area
 */
export const buildEquipmentState = (state: any, filteredLayout: Record<string, any>): any => {
  const { activeTab } = state.container;
  const current = state.container.areas[activeTab];

  const updatedArea = {
    ...current,
    ...(filteredLayout.microphones && {
      microphones: { ...current.microphones, ...filteredLayout.microphones },
    }),
    ...(filteredLayout.speakers && {
      speakers: { ...current.speakers, ...filteredLayout.speakers },
    }),
    ...(filteredLayout.cameras && {
      cameras: { ...current.cameras, ...filteredLayout.cameras },
    }),
  };

  return {
    ...state,
    container: {
      ...state.container,
      areas: { ...state.container.areas, [activeTab]: updatedArea },
    },
  };
};
